// P-PATHHOME-BUG-2-ROUND-2 — canonical persistence identity (React hook).
//
// Wraps the pure resolveStudentIdAction with the async side effects:
// lazy-create a cloud students row + link it to the local child the
// FIRST time an attempt needs to persist and the child isn't linked
// yet. Returns ensureActiveStudentId() — the single async resolver the
// 4 session screens call before writing a child_attempts row.
//
// Watch-outs (Alex, P-PATHHOME-BUG-2-ROUND-2):
//   (1) Idempotency: an in-flight guard keyed by local child id means
//       concurrent first-attempts (and rapid sequential ones) await the
//       SAME create promise — never two students rows for one child.
//       The guard also rate-limits: at most one create in flight at a
//       time; a sequential retry only starts after the prior settles.
//   (2) Failure fallback: if createCloudStudent fails (cloud offline,
//       RLS, timeout) the resolver returns null and the caller SKIPS
//       that attempt (drops it, logs for observability) — it never
//       throws into the play UX. A later attempt retries the create, so
//       a mid-session cloud recovery still links the kid.
//   (3) Load-gating: lazy-create fires ONLY when the link is genuinely
//       null after auth + cloud have settled (resolveStudentIdAction
//       returns "pending" while cloudLoading, so the auto-link-by-name
//       pass gets its chance first).
//
// Known limitation (disclosed, out of scope here): the in-flight guard
// is per hook instance (one App). It does NOT dedupe across browser
// tabs/devices — two tabs racing the very first attempt could each
// create a student. createStudent has no name-based unique constraint;
// auto-link-by-name reconciles the common returning-kid case before we
// ever reach "create". A cross-tab dedup (unique index / upsert) is a
// separate hardening item.

import { useCallback, useRef } from "react";
import { resolveStudentIdAction } from "./persistenceIdentity";
import { pickLazyReuseRow, childrenHoldingRow } from "./sharedRowGuard.js";
import { listChildren } from "../childrenStore.js";

/**
 * Async orchestrator for the canonical-id resolution — extracted from the
 * hook so the load-bearing side-effect paths (idempotency guard, lazy
 * create, failure fallback) are unit-testable WITHOUT React. The hook is a
 * thin useCallback wrapper around this; tests pass a plain `{ current }`
 * object for `inflightRef` (exactly what useRef provides at runtime).
 *
 * @param {Object} deps
 * @param {{current: ({key:string, promise:Promise<string|null>}|null)}} deps.inflightRef  mutable ref ({current})
 * @param {string|null|undefined} deps.activeSupabaseStudentId
 * @param {{id:string}|null|undefined} deps.user
 * @param {boolean} deps.cloudLoading
 * @param {{id:string,name?:string,age?:number|null}|null|undefined} deps.activeChild
 * @param {Array<{id:string,name?:string,age?:number|null}>|null|undefined} deps.cloudStudents  the parent's already-loaded cloud students (for lookup-before-create)
 * @param {Object|null|undefined} deps.progress
 * @param {(parentId:string, data:Object)=>Promise<{id:string}|null>} deps.createCloudStudent
 * @param {(()=>{children:Array})|undefined} deps.getRegistry  test seam; defaults to the real registry read (MA-800)
 * @param {(localChildId:string, cloudStudentId:string)=>void} deps.onLinked
 * @returns {Promise<string|null>}
 */
// MA-800 latch: once a link is refused for (local kid, row), stop retrying for the
// rest of the session (each retry would INSERT + 23505 lookup + a report on every
// answered item). Keyed by localChildId; the latched row is re-checked so it
// self-heals if the other kid releases it. Reset hook is for tests.
const refusedLinks = new Map(); // localChildId -> rowId
export function __resetRefusedLinkLatchForTests() { refusedLinks.clear(); }
function latchedRefusal(childId, registry) {
  const rowId = refusedLinks.get(childId);
  if (!rowId) return false;
  if (childrenHoldingRow(registry, rowId, childId).length > 0) return true;
  refusedLinks.delete(childId);
  return false;
}

function refusedLink(childId, rowId) {
  if (childId && rowId) refusedLinks.set(childId, rowId);
  console.error(
    "[persistenceIdentity] link refused (cloud row already linked to another local child) - attempt not persisted to the cloud",
  );
  return null;
}

export async function resolveCanonicalStudentId({
  inflightRef,
  activeSupabaseStudentId,
  user,
  cloudLoading,
  activeChild,
  cloudStudents,
  progress,
  createCloudStudent,
  onLinked,
  // MA-800: defaults to the REAL registry read, so the shared-row guard can
  // never be silently off (no caller has to remember to pass it). Injectable
  // for tests only.
  getRegistry = listChildren,
}) {
  const action = resolveStudentIdAction({
    activeSupabaseStudentId,
    user,
    cloudLoading,
    activeChild,
  });

  if (action.kind === "ready") return action.id;

  if (action.kind !== "create") {
    // pending (still loading — skip, retry next attempt) or unavailable
    // (no user / no active child). Either way: no persist this attempt.
    if (action.kind === "unavailable") {
      console.warn(
        `[persistenceIdentity] cannot resolve canonical student id (${action.reason}) — attempt not persisted`,
      );
    }
    return null;
  }

  // P-DUP-STUDENT-FIX — lookup-before-create. resolveStudentIdAction
  // returns "create" whenever the link is null after load settled — but
  // that's the SAME signal a transient auto-link-by-name MISS produces:
  // a name-normalization mismatch, an age field that differs, or a
  // momentarily-empty cloud list after a network blip. Inserting a row on
  // that signal is exactly what bled 21 orphan duplicate students for one
  // auth user (BUG 2 Chrome MCP probe — see
  // scripts/cleanup-orphan-students.mjs). So BEFORE we INSERT, scan the
  // already-loaded cloud students for an existing row this parent owns
  // that matches by name (age narrows ties) and reuse it. This lives in
  // the impure hook layer — NOT in resolveStudentIdAction — because it
  // reads cloud state; the pure core stays pure + deterministic.
  //
  // norm mirrors childrenStore.autoLinkByNameAge exactly so the two
  // matchers agree (a row the auto-link pass would have bound is the same
  // row we reuse here).
  // MA-800: skip rows the registry already links to ANOTHER local child
  // (pickLazyReuseRow) — reusing one would make two kids share a cloud row.
  const registry = getRegistry();
  // MA-800: a refusal already seen this session for this kid -> no create, no report.
  if (latchedRefusal(activeChild.id, registry)) return null;
  const reuse = pickLazyReuseRow(
    registry,
    cloudStudents,
    activeChild,
  );
  // 0 matches (a genuine first-time create) OR ambiguous (>1 name match
  // with no age to narrow) → fall through to the existing create flow.
  if (reuse && reuse.id) {
    if (onLinked(activeChild.id, reuse.id) === false) return refusedLink(activeChild.id, reuse.id);
    console.warn(
      "[persistenceIdentity] reused existing cloud student via lookup-before-create — skipped duplicate INSERT",
    );
    return reuse.id;
  }

  const key = activeChild.id;
  // Idempotency + rate-limit: reuse an in-flight create for this child.
  if (inflightRef.current && inflightRef.current.key === key) {
    return inflightRef.current.promise;
  }

  const promise = (async () => {
    // createStudent applies its own column defaults (grade →
    // 'Kindergarten', math/reading level ids) when these are undefined,
    // so a sparse progress object still produces a valid row.
    const row = await createCloudStudent(user.id, {
      name: activeChild.name,
      age: activeChild.age ?? progress?.studentAge ?? null,
      grade: progress?.studentGrade,
      mathLevelId: progress?.mathModuleId,
      readingLevelId: progress?.readingModuleId,
    });
    if (!row || !row.id) {
      // Failure fallback (watch-out 2): drop this attempt, surface for
      // observability, never throw into the session UX.
      console.error(
        "[persistenceIdentity] lazy createCloudStudent failed — attempt dropped (will retry on next attempt)",
      );
      return null;
    }
    // Link local → cloud so subsequent renders resolve "ready" and all
    // reads (trail, mastery, …) key off the same canonical id.
    // MA-800: a refused link (the row is already held by another local kid, e.g.
    // createStudent's 23505 recovery returned a same-name sibling's row) means
    // NO id: never write this kid's attempts under another kid's row.
    if (onLinked(activeChild.id, row.id) === false) return refusedLink(activeChild.id, row.id);
    return row.id;
  })();

  inflightRef.current = { key, promise };
  try {
    return await promise;
  } finally {
    // Clear only if we're still the owner (a later child switch may
    // have replaced it). Failed creates clear too, so the next attempt
    // can retry a fresh create.
    if (inflightRef.current && inflightRef.current.key === key) {
      inflightRef.current = null;
    }
  }
}

/**
 * @param {Object} deps
 * @param {{id:string}|null|undefined} deps.user
 * @param {{id:string,name?:string,age?:number|null}|null|undefined} deps.activeChild
 * @param {string|null|undefined} deps.activeSupabaseStudentId
 * @param {Array<{id:string,name?:string,age?:number|null}>|null|undefined} deps.cloudStudents  the parent's loaded cloud students (lookup-before-create)
 * @param {Object|null|undefined} deps.progress  active child's progress (module ids, grade, age)
 * @param {boolean} deps.cloudLoading
 * @param {(parentId:string, data:Object)=>Promise<{id:string}|null>} deps.createCloudStudent
 * @param {(localChildId:string, cloudStudentId:string)=>void} deps.onLinked  persist + refresh the local→cloud link
 * @returns {() => Promise<string|null>}  ensureActiveStudentId
 */
export default function useActiveStudentId({
  user,
  activeChild,
  activeSupabaseStudentId,
  cloudStudents,
  progress,
  cloudLoading,
  createCloudStudent,
  onLinked,
}) {
  // { key: localChildId, promise: Promise<string|null> } | null
  const inflightRef = useRef(null);

  const ensureActiveStudentId = useCallback(
    () =>
      resolveCanonicalStudentId({
        inflightRef,
        activeSupabaseStudentId,
        user,
        cloudLoading,
        activeChild,
        cloudStudents,
        progress,
        createCloudStudent,
        onLinked,
      }),
    [
      activeSupabaseStudentId,
      user,
      cloudLoading,
      activeChild,
      cloudStudents,
      progress,
      createCloudStudent,
      onLinked,
    ],
  );

  return ensureActiveStudentId;
}
